import Const from 'constants';
import {initSportConfig} from 'helpers/common-helper';

let sConfig = initSportConfig();
let sport = sConfig['sport'] || '';
let site = sConfig['site'] || '';
let slateId = sConfig['slateId'] || '';
let gameType = sConfig['gameType'] || Const.GAME_TYPE_CLASSIC;
let mode = sConfig['mode'];

if (!mode || !Const.MODES.hasOwnProperty(mode) || !Boolean(__preSettings__.modes?.[mode].isUsed ?? false)) {
    mode = Const.MODE_MAIN;
    sport = '';
    site = '';
    slateId = '';
    gameType = Const.GAME_TYPE_CLASSIC;
}

const defaultStateData = {
    config: {
        gameTypes: [],
        positions: {},
        restrictions: {},
    },
    sport,
    site,
    slateId,
    gameType,
    mode,
    loading: false,
};

class State {
    config = {
        gameTypes: [],
        positions: {},
        restrictions: {},
    };
    sportsMap = __preSettings__.sportsMap;
    sport;
    site;
    slateId;
    gameType;
    mode;
    error;
    loading;
    constructor(data) {
        const {config, mode, site, sport, gameType, slateId, loading = false, error = null} = data;

        this.config = config;
        this.sport = sport;
        this.sport = sport;
        this.site = site;
        this.slateId = slateId;
        this.gameType = gameType;
        this.mode = mode;
        this.loading = loading;
        this.error = error;
    }
    getState = () => {
        return {
            config: this.config,
            sport: this.sport,
            site: this.site,
            mode: this.mode,
            gameType: this.gameType,
            slateId: this.slateId,
            loading: this.loading,
        };
    };
    isBacktestModeUsed = () => Boolean(__preSettings__.modes[Const.MODE_BACKTEST].isUsed);
    isLateswapModeUsed = () => Boolean(__preSettings__.modes[Const.MODE_LATESWAP].isUsed);
    isModeUsed = (mode) => Boolean(__preSettings__.modes?.[mode].isUsed ?? false);
    hasRestrictions = () => !isEmpty(this.config.restrictions);
    useGameFilter = () => this.hasRestrictions() && this.config.restrictions.useGameFilter;
    usePositionFilter = () => this.hasRestrictions() && this.config.restrictions.usePositionFilter;
    useIntervalUpdate = () => this.hasRestrictions() && this.config.restrictions.useIntervalUpdate;
    getConfigProp = (name, defaultValue = null) => {
        if (this.hasRestrictions() && this.config.restrictions.hasOwnProperty(name)) {
            const value = this.config.restrictions[name];

            return value === null ? defaultValue : value;
        }

        return defaultValue;
    };
    getFilter = (name, defaultValue = null, parameters = {}) => {
        if (name === 'positions') {
            const {withFlex = false} = parameters;
            const positions = this.config?.filter?.positions ?? [];

            return withFlex ? positions : positions.filter((position) => !inArray(position.key, ['FLEX', 'S-FLEX']));
        }

        return this.config?.filter?.[name] ?? defaultValue;
    };
    getPositions = () => ({...this.config.positions});
    getPositionNumberByName = (positionName) => {
        const {positions} = this.config;

        for (const positionNumber in positions) {
            if (positions[positionNumber].toUpperCase() === positionName.toUpperCase()) {
                return positionNumber;
            }
        }

        return null;
    };
    getJointPosition = (position, defaultValue = null) => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        if (position in positions && positions[position].jointPosition) {
            return positions[position].jointPosition;
        }

        return defaultValue;
    };
    getMaxPlayersOnPosition = (position, defaultValue = 0) => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        if (position in positions && positions[position].maxPlayers) {
            return positions[position].maxPlayers;
        }

        return defaultValue;
    };
    getPositionName = (position, defaultValue = '') => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        if (position in positions && positions[position].shortName) {
            return positions[position].shortName;
        }

        return defaultValue;
    };
    getPositionSynonym = (position, defaultValue = false) => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        if (position in positions && positions[position].synonymName) {
            return positions[position].synonymName;
        }

        return defaultValue;
    };
    getPositionByName = (name, defaultValue = {}) => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        for (let position of Object.values(positions)) {
            if (position.shortName === name) {
                return {...position};
            }
        }

        return defaultValue;
    };
    getPositionByKey = (key, defaultValue = {}) => {
        const positions = this.getConfigProp('positions', {});

        if (!isEmpty(positions) && positions.hasOwnProperty(key)) {
            return {...positions[key]};
        }

        return defaultValue;
    };
    getPositionById = (id, defaultValue = {}) => {
        const positions = this.getConfigProp('positions', {});

        if (isEmpty(positions)) {
            return defaultValue;
        }

        for (let position of Object.values(positions)) {
            if (position.id === id) {
                return {...position};
            }
        }

        return defaultValue;
    };
    getAllowedPositionsByPositionInLineup = (positionInLineup) => {
        let positions = this.getConfigProp('positionsOrder', {});

        if (isEmpty(positions)) {
            return [];
        }

        for (let position of Object.values(positions)) {
            if (position.alias === positionInLineup) {
                return [...position.allowedPositions];
            }
        }

        return [];
    };
    getShadingOptions = () => (this.hasRestrictions() ? this.config.restrictions.shadingOptions : {});
    getMaxSalaryCap = () => (this.hasRestrictions() ? this.config.restrictions.maxSalary : 0);
    getMinSalaryCap = () => {
        if (!this.hasRestrictions()) {
            return 0;
        }

        return this.config.restrictions.minSalary ?? 0;
    };
    getPlayersInTeam = () => (this.hasRestrictions() ? Number(this.config.restrictions.playersInTeam) : 0);
    isReady = () => this.hasSport() && this.hasSite() && this.hasSlateId();
    isSiteSportGameReady = () => this.hasSport() && this.hasSite() && this.hasGameType();
    isSiteSportGameEqual = (sp) => {
        return (
            this.isSiteEqual(sp.getSite()) && this.isSportEqual(sp.getSport()) && this.isGameTypeEqual(sp.getGameType())
        );
    };
    isSiteDraftkings = () => this.getSite() === Const.SITE_DK;
    isSiteFanduel = () => this.getSite() === Const.SITE_FD;
    isSiteYahoo = () => this.getSite() === Const.SITE_YAHOO;
    isSiteEqual = (site) => this.getSite() === site;
    isSportCfb = () => this.getSport() === Const.LEAGUE_CFB;
    isSportGolf = () => this.getSport() === Const.LEAGUE_GOLF;
    isSportF1 = () => this.getSport() === Const.LEAGUE_F1;
    isSportLol = () => this.getSport() === Const.LEAGUE_LOL;
    isSportMma = () => this.getSport() === Const.LEAGUE_MMA;
    isSportMlb = () => this.getSport() === Const.LEAGUE_MLB;
    isSportNascar = () => this.getSport() === Const.LEAGUE_NASCAR;
    isSportNba = () => this.getSport() === Const.LEAGUE_NBA;
    isSportNfl = () => this.getSport() === Const.LEAGUE_NFL;
    isSportNhl = () => this.getSport() === Const.LEAGUE_NHL;
    isSportTennis = () => this.getSport() === Const.LEAGUE_TENNIS;
    isSportEqual = (sport) => this.getSport() === sport;
    isGameTypeEqual = (gameType) => this.getGameType() === gameType;
    isGameTypeClassic = () => this.getGameType() === Const.GAME_TYPE_CLASSIC;
    isGameTypeShowDown = () => this.getGameType() === Const.GAME_TYPE_SHOWDOWN;
    isGameTypeSingleGame = () => this.getGameType() === Const.GAME_TYPE_SINGLEGAME;
    isGameTypeTiers = () => this.getGameType() === Const.GAME_TYPE_TIERS;
    isGameTypeShowDownOrSingleGame = () => this.isGameTypeShowDown() || this.isGameTypeSingleGame();
    isModeBacktest = () => this.getMode() === Const.MODE_BACKTEST;
    isModeMain = () => this.getMode() === Const.MODE_MAIN;
    isModeLateswap = () => this.getMode() === Const.MODE_LATESWAP;
    isSlateIdEqual = (slateId) => String(this.getSlateId()) === String(slateId);
    isModeEqual = (mode) => this.getMode() === mode;
    hasSport = () => this.sport.length > 0;
    hasSite = () => this.site.length > 0;
    hasSlateId = () => !!this.slateId;
    hasGameType = () => this.gameType.length > 0;
    getSites = () => Object.keys(this.sportsMap);
    getSportsBySite = (site) => [...(this.sportsMap?.[site] ?? [])];
    getMode = () => this.mode;
    getSport = () => this.sport;
    getSite = () => this.site;
    getSlateId = () => this.slateId;
    getGameType = () => this.gameType;
    getGameTypes = () => this.config.gameTypes;
    isLoading = () => this.loading;
    isUserPointsTypeUsed = () => {
        const leagues = [
            Const.LEAGUE_NBA,
            Const.LEAGUE_NFL,
            Const.LEAGUE_GOLF,
            Const.LEAGUE_MMA,
            Const.LEAGUE_LOL,
            Const.LEAGUE_TENNIS,
        ];

        return leagues.includes(this.getSport());
    };
}

const defaultState = new State(defaultStateData);

export default function sportConfig(state = defaultState, action) {
    if (action.type === Const.GET_SPORT_CONFIG_REQUEST) {
        state = new State({...state.getState(), loading: true});
    } else if (action.type === Const.GET_SPORT_CONFIG_SUCCESS) {
        state = new State({...state.getState(), config: action.data, loading: false});
    } else if (action.type === Const.GET_SPORT_CONFIG_FAILURE) {
        state = new State({...state.getState(), config: action.data, loading: false, error: action.error});
    } else if (action.type === Const.UPDATE_MAX_SALARY) {
        if (state.hasRestrictions() && 'maxSalary' in state.config.restrictions) {
            const config = cp(state.config);
            config.restrictions.maxSalary = action.data;
            state = new State({...state.getState(), config});
        }
    } else if (action.type === Const.FILTER_BY_MODE) {
        state = new State({
            ...state.getState(),
            config: {...cp(state.config), gameTypes: []},
            mode: action.mode,
            site: '',
            sport: '',
            gameType: Const.GAME_TYPE_CLASSIC,
            slateId: '',
        });
    } else if (action.type === Const.FILTER_BY_SITE) {
        state = new State({
            ...state.getState(),
            config: {...cp(state.config), gameTypes: []},
            site: action.site,
            sport: action.sport ?? '',
            gameType: Const.GAME_TYPE_CLASSIC,
            slateId: '',
        });
    } else if (action.type === Const.FILTER_BY_SPORT) {
        state = new State({
            ...state.getState(),
            config: {...cp(state.config), gameTypes: []},
            sport: action.sport,
            gameType: Const.GAME_TYPE_CLASSIC,
            slateId: '',
        });
    } else if (action.type === Const.FILTER_BY_GAME_TYPE) {
        state = new State({
            ...state.getState(),
            gameType: action.gameType,
            slateId: '',
        });
    } else if (action.type === Const.FILTER_BY_SLATE) {
        state = new State({...state.getState(), slateId: action.slateId});
    }

    return state;
}
